import { captureException, setContext } from "@sentry/nextjs";
import { useState, ReactNode, useEffect, useMemo } from "react";

import { IDENTITY_AUTHENTICATED_COOKIE_NAME } from "@framework/config/identity.config";
import AuthContext, {
  IdentityUser,
} from "@voxmedia/duet-context/context/auth-context";
import ServerAuthContext, {
  ServerAuthState,
} from "@voxmedia/duet-context/context/server-auth-context";
import { getCookie } from "@voxmedia/duet-utils/cookie";

interface Props {
  children?: ReactNode;
  serverAuthState?: ServerAuthState;
}

interface VerificationResponse {
  user?: IdentityUser; // Legacy format for backwards compatibility. TODO: [identity] Remove this after all clients are updated.
  data?: { user?: IdentityUser };
}

export const FirebaseIdentityProvider = ({
  children,
  serverAuthState,
}: Props) => {
  const [identityUser, setIdentityUser] = useState<IdentityUser | null>(null);
  const [userLoading, setUserLoading] = useState<boolean>(true);

  const setUser = (user: IdentityUser | null) => {
    setIdentityUser(user);
  };

  // Run once on initial render.
  useMemo(() => {
    // Set default context.
    setContext("Duet User", {
      loggedIn: false,
      service: "firebase",
    });
  }, []);

  useEffect(() => {
    if (identityUser) {
      setContext("Duet User", {
        loggedIn: true,
        // Add non-sensitive data.
        emailVerified: identityUser.emailVerified,
        entitlements: identityUser.entitlements,
        isEntitled: identityUser.isEntitled,
        hasZephrJwtToken: Boolean(identityUser.zephrJwt),
        service: "firebase",
      });
    } else {
      setContext("Duet User", {
        loggedIn: false,
        service: "firebase",
      });
    }
  }, [identityUser]);

  useEffect(() => {
    const verifyUser = async (retries: number = 2, delay: number = 500) => {
      const response = await fetch("/api/auth/firebase-verify", {
        method: "post",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "same-origin",
      });
      if (response.ok) {
        const verificationResponse =
          (await response.json()) as VerificationResponse;
        // TODO: [identity] Remove the legacy format after all clients are updated.
        const user =
          verificationResponse.data?.user ?? verificationResponse.user ?? null;

        setIdentityUser(user);
      } else if ([400, 401].includes(response.status)) {
        setIdentityUser(null);
      } else {
        // If the user isn't verified or specifically unauthenticated, retry
        if (retries > 0) {
          await new Promise((resolve) => setTimeout(resolve, delay));
          return verifyUser(retries - 1, delay * 3);
        }
        setIdentityUser(null);
      }
    };

    const verifySession = async () => {
      const isAuthenticated =
        getCookie(IDENTITY_AUTHENTICATED_COOKIE_NAME) === "true";

      if (!isAuthenticated) {
        setUserLoading(false);
        return;
      }

      try {
        await verifyUser();
      } catch (error) {
        captureException(error, {
          extra: {
            message: "Failed to verify identity session",
          },
        });
      }

      setUserLoading(false);
    };

    void verifySession();
  }, []);

  return (
    <ServerAuthContext.Provider value={serverAuthState || null}>
      <AuthContext.Provider
        value={{
          authMethod: "identity",
          user: identityUser,
          userLoading,
          setUser,
        }}
      >
        {children}
      </AuthContext.Provider>
    </ServerAuthContext.Provider>
  );
};
